import { useContext, useEffect, useRef, useState } from "react";
import NumberBox from "../../components/ui/number-box/numberbox";
import * as api from "../../utils/Api.ts";
import { InitDataContext } from "../../context/InitDataContext";
import Radar from "../../components/widgets/graphs/Radar";
import "../../utils/main.css";
import VerticalBar from "../../components/widgets/graphs/Bars.tsx";
import TableRecentsStatistics from "../../components/widgets/TableRecentItems.tsx";
import { Toast } from "primereact/toast";
import { FORMAT_THOUSANDS, showMsg } from "../../utils/Constants.tsx";

const labelsMonths = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];
const labelsYear = [new Date().getFullYear()];

export default function IndexOrdenCompra() {
  interface PurchaseOrdersStatistics {
    total: number;
    pendientes: number;
    anuladas: number;
    rechazadas_parcial: number;
    rechazadas: number;
    aprobadas_parcial: number;
    aprobadas: number;
    confirmadas_parcial: number;
    confirmadas: number;
    pendientes_gte: number;
    confirmadas_gte: number;
  }

  const [isLoadingMonth, setIsLoadingMonth] = useState(true);
  const [isLoadingYear, setIsLoadingYear] = useState(true);
  const [isLoadingPptoData, setIsLoadingPptoData] = useState(true);
  const purchaseOrdersStatisticsMonth =
    useRef<PurchaseOrdersStatistics | null>();
  const purchaseOrdersStatisticsYear =
    useRef<PurchaseOrdersStatistics | null>();

  const pptoDataStatisticsMonth = useRef<Record<number, any> | null>();
  const pptoDataStatisticsYear = useRef<Record<number, any> | null>();
  const purchaseOrdersPptoStatisticsMonth = useRef();
  const purchaseOrdersPptoStatisticsYear = useRef();
  const purchaseOrdersAllPptoStatisticsMonths = useRef<Record<
    string,
    any
  > | null>(null);
  const jsonArrayGraphPptoData = useRef<number[] | undefined>();
  const jsonArrayGraphPurchaseOrdersPptoYear = useRef<number[] | undefined>();
  const jsonArrayGraphPptoDataBase = useRef<number[] | undefined>();
  const jsonArrayGraphPptoDataBaseYear = useRef<number[] | undefined>();
  const dataRecentOrdenes = useRef<any[]>([]);
  const dataStatisticsOrdenesCentros = useRef<any[]>([]);
  const [isLoadingFooterDataRecents, setIsLoadingFooterDataRecents] =
    useState(true);
  const [isLoadingFooterDataCentros, setIsLoadingFooterDataCentros] =
    useState(true);

  const { initData } = useContext(InitDataContext);
  const CENTRO_COSTO_USUARIO = initData?.centro_costo.toUpperCase();
  const CENTRO_COSTO_VISTA_USUARIO =
    initData?.centros_costo_vista_nombre.toUpperCase();
  const toast = useRef(null);
  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();
  const formatMonthYearTwoDigit =
    currentMonth < 10
      ? `0${currentMonth}-${currentYear}`
      : `${currentMonth}-${currentYear}`;
  const formatYear = `-${currentYear}`;

  const rolesDeconstructedVal = initData.roles.some(
    (role: { codigo: string }) =>
      role.codigo === "APROC" || role.codigo === "CONFOC",
  );

  const subroganteVal =
    !!initData.mi_subrogancia && initData.mi_subrogancia.trim().length > 0
      ? true
      : false;

  useEffect(() => {
    const fetchPurchaseOrdersStatistics = async () => {
      try {
        //ESTADISTICA MENSUAL
        const responseGetPurchaseOrdersStatisticsMonth =
          await api.GET_ORDENES_COMPRA_STATISTICS_MONTH.getOrdenesCompraStatisticsMonth(
            initData.id,
            initData.id_centro_costo,
            initData.centros_costo_vista,
            rolesDeconstructedVal,
            subroganteVal,
          );
        if (responseGetPurchaseOrdersStatisticsMonth !== null) {
          purchaseOrdersStatisticsMonth.current =
            responseGetPurchaseOrdersStatisticsMonth;
          setIsLoadingMonth(false);
        } else {
          showMsg(
            toast,
            "error",
            "Error",
            "Error capturando datos mensuales de las ordenes por centro costo y usuario",
          );
        }

        //ESTADISTICA ANUAL
        const responseGetPurchaseOrdersStatistics =
          await api.GET_ORDENES_COMPRA_STATISTICS_YEAR.getOrdenesCompraStatisticsYear(
            initData.id,
            initData.id_centro_costo,
            initData.centros_costo_vista,
            rolesDeconstructedVal,
            subroganteVal,
          );
        if (responseGetPurchaseOrdersStatistics !== null) {
          purchaseOrdersStatisticsYear.current =
            responseGetPurchaseOrdersStatistics;
          setIsLoadingYear(false);
        } else {
          showMsg(
            toast,
            "error",
            "Error",
            "Error capturando datos anuales de las ordenes por centro costo y usuario",
          );
        }
      } catch (error) {
        console.error("Error fetching ordenes statistics:", error);
      }
    };

    const fetchRecentOrdenes = async () => {
      try {
        const responseGetRecentOrdenes =
          await api.GET_STATISTICS_RECENT_ORDENES.getStatisticsRecentOrdenes(
            initData.id,
          );
        if (responseGetRecentOrdenes !== null) {
          dataRecentOrdenes.current = responseGetRecentOrdenes;
          setIsLoadingFooterDataRecents(false);
        } else {
          showMsg(
            toast,
            "error",
            "Error",
            "Error capturando datos de las ordenes más recientes",
          );
        }
      } catch (error) {
        console.error("Error fetching recent ordenes:", error);
      }
    };

    const fetchStatisticsOrdenesByCentros = async () => {
      try {
        const responseGetStatisticsCentros =
          await api.GET_STATISTICS_ORDENES_COST_CENTER.getStatisticsOrdenesCostCenter(
            initData.id,
          );
        if (responseGetStatisticsCentros !== null) {
          dataStatisticsOrdenesCentros.current = responseGetStatisticsCentros;
          setIsLoadingFooterDataCentros(false);
        } else {
          showMsg(
            toast,
            "error",
            "Error",
            "Error capturando datos de las ordenes de los centros más demandantes",
          );
        }
      } catch (error) {
        console.error(
          "Error fetching recent ordenes from demanding centers:",
          error,
        );
      }
    };

    const fetchPptoStatistics = async () => {
      try {
        //ESTADISTICA MENSUAL
        /*const responseGetPptoStatisticsMonth =
          await api.GET_PPTO_STATISTICS_MONTH.getPptoStatisticsMonth(
            initData.id,
            initData.id_centro_costo,
          );
        if (responseGetPptoStatisticsMonth !== null) {
          pptoDataStatisticsMonth.current = responseGetPptoStatisticsMonth;
        } else {
          showMsg(
            toast,
            "error",
            "Error",
            "Error capturando datos mensuales de presupuesto",
          );
        }*/

        /* const responseGetPurchaseOrdersPptoStatisticsMonth =
          await api.GET_ORDENES_COMPRA_PPTO_STATISTICS_YEAR.getOrdenesCompraPptoStatisticsYear(
            initData.id,
            initData.id_centro_costo,
          );
        if (responseGetPurchaseOrdersPptoStatisticsMonth !== null) {
          purchaseOrdersPptoStatisticsMonth.current =
            responseGetPurchaseOrdersPptoStatisticsMonth;
        } else {
          showMsg(
            toast,
            "error",
            "Error",
            "Error capturando datos mensuales de suma solicitudes ref presupuesto",
          );
        }*/

        const responseGetAllPptoPurchaseOrdersMonths =
          await api.GET_ALL_PPTO_ORDENES_COMPRA_MONTHS.getAllPptoOrdenesCompraMonths(
            initData.id,
            initData.id_centro_costo,
            initData.centros_costo_vista,
            rolesDeconstructedVal,
            subroganteVal,
          );

        if (responseGetAllPptoPurchaseOrdersMonths !== null) {
          purchaseOrdersAllPptoStatisticsMonths.current =
            responseGetAllPptoPurchaseOrdersMonths;

          //CREAMOS EL ARREGLO PARA LOS GRAFICOS DE PRESUPUESTO POR SOLICITUDES REFERENCIALES
          const jsonArrayGraphPpto = [];
          const jsonArrayGraphPptoYear = [];
          const obj = purchaseOrdersAllPptoStatisticsMonths.current || {};

          for (const key in obj) {
            let value = 0;
            let valueYearly = 0;

            if (obj[key] !== null) {
              value = parseFloat(obj[key]);
            }
            if (key === "total" || key === "total_ano_pasado") {
              valueYearly = obj[key];
              jsonArrayGraphPptoYear.push(valueYearly);
            } else {
              jsonArrayGraphPpto.push(value);
            }
          }
          jsonArrayGraphPptoData.current = jsonArrayGraphPpto;
          jsonArrayGraphPurchaseOrdersPptoYear.current = jsonArrayGraphPptoYear;
        } else {
          showMsg(
            toast,
            "error",
            "Error",
            "Error capturando datos mensuales de suma solicitudes ref presupuesto",
          );
        }

        //ESTADISTICA ANUAL
        const responseGetPptoStatisticsYear =
          await api.GET_PPTO_STATISTICS_YEAR.getPptoStatisticsYear(
            initData.id,
            initData.id_centro_costo,
            initData.centros_costo_vista,
            rolesDeconstructedVal,
            subroganteVal,
          );
        if (responseGetPptoStatisticsYear !== null) {
          pptoDataStatisticsYear.current = responseGetPptoStatisticsYear;

          //CREAMOS EL ARREGLO PARA LOS GRAFICOS DE PRESUPUESTO
          const jsonArrayGraphPptoBase = [];
          const jsonArrayGraphPptoBaseYear = [];
          const obj = pptoDataStatisticsYear.current || {};
          for (const key in obj) {
            let value = 0;
            let valueYearly = 0;
            if (obj[key] !== null) {
              value = obj[key];
            }
            if (key === "total" || key === "ppto_ano_anterior") {
              valueYearly = obj[key];
              jsonArrayGraphPptoBaseYear.push(valueYearly);
            } else {
              jsonArrayGraphPptoBase.push(value);
            }
          }
          //ESTADISTICA DE PRESUPUESTO AÑO EN CURSO POR MES
          jsonArrayGraphPptoDataBase.current = jsonArrayGraphPptoBase;
          //ESTADISTICA PARA GRAFICO DE PRESUPUESTO TOTAL AÑO ANTERIOR VS AÑO EN CURSO
          jsonArrayGraphPptoDataBaseYear.current = jsonArrayGraphPptoBaseYear;
        } else {
          showMsg(
            toast,
            "error",
            "Error",
            "Error capturando datos anuales de presupuesto",
          );
        }

        const responseGetPurchaseOrdersPptoStatisticsYear =
          await api.GET_ORDENES_COMPRA_PPTO_STATISTICS_YEAR.getOrdenesCompraPptoStatisticsYear(
            initData.id,
            initData.id_centro_costo,
            initData.centros_costo_vista,
            rolesDeconstructedVal,
            subroganteVal,
          );
        if (responseGetPurchaseOrdersPptoStatisticsYear !== null) {
          purchaseOrdersPptoStatisticsYear.current =
            responseGetPurchaseOrdersPptoStatisticsYear;

          setIsLoadingPptoData(false);
        } else {
          showMsg(
            toast,
            "error",
            "Error",
            "Error capturando datos anuales de suma solicitudes ref presupuesto",
          );
        }
      } catch (error) {
        console.error("Error fetching budget statistics:", error);
      }
    };
    fetchPurchaseOrdersStatistics();
    fetchPptoStatistics();
    fetchRecentOrdenes();
    fetchStatisticsOrdenesByCentros();
  }, []);

  const renderPurchaseOrdersStatesMonth = () => {
    if (purchaseOrdersStatisticsMonth.current) {
      return (
        <div className="rowContainer">
          <NumberBox
            title="Totales"
            value={
              purchaseOrdersStatisticsMonth.current
                ? FORMAT_THOUSANDS(purchaseOrdersStatisticsMonth.current.total)
                : 0
            }
            backColor={"--primary-app-color"}
            url={`ordenes-compra/mantencion?ocest_nombre=&oc_dt_cre=-${formatMonthYearTwoDigit}`}
          />
          <NumberBox
            title="Pendientes"
            value={
              purchaseOrdersStatisticsMonth.current
                ? purchaseOrdersStatisticsMonth.current.pendientes
                : 0
            }
            backColor={"--color-pendiente"}
            url={`ordenes-compra/mantencion?ocest_nombre=Pendiente&oc_dt_cre=-${formatMonthYearTwoDigit}`}
          />
          <NumberBox
            title="Anuladas"
            value={
              purchaseOrdersStatisticsMonth.current
                ? purchaseOrdersStatisticsMonth.current.anuladas
                : 0
            }
            backColor={"--color-anuladas"}
            url={`ordenes-compra/mantencion?ocest_nombre=Anulada&oc_dt_cre=-${formatMonthYearTwoDigit}`}
          />
          {/* <NumberBox
            title="Rechazadas parcial"
            value={
              purchaseOrdersStatisticsMonth.current
                ? purchaseOrdersStatisticsMonth.current.rechazadas_parcial
                : 0
            }
            backColor={"--color-rechazadas-parcial"}
            url={`ordenes-compra/mantencion?ocest_nombre=Rechazada parcialmente&oc_dt_cre=-${formatMonthYearTwoDigit}`}
          />
          */}
          <NumberBox
            title="Rechazadas"
            value={
              purchaseOrdersStatisticsMonth.current
                ? purchaseOrdersStatisticsMonth.current.rechazadas
                : 0
            }
            backColor={"--color-rechazadas"}
            url={`ordenes-compra/mantencion?ocest_nombre=Rechazada&oc_dt_cre=-${formatMonthYearTwoDigit}`}
          />
          {/*<NumberBox
            title="Aprobadas parcial"
            value={
              purchaseOrdersStatisticsMonth.current
                ? purchaseOrdersStatisticsMonth.current.aprobadas_parcial
                : 0
            }
            backColor={"--color-aprobadas-parcial"}
            url={`ordenes-compra/mantencion?ocest_nombre=Aprobada parcialmente&oc_dt_cre=-${formatMonthYearTwoDigit}`}
          />*/}
          <NumberBox
            title="Aprobadas"
            value={
              purchaseOrdersStatisticsMonth.current
                ? purchaseOrdersStatisticsMonth.current.aprobadas
                : 0
            }
            backColor={"--color-aprobadas"}
            url={`ordenes-compra/mantencion?ocest_nombre=Aprobada&oc_dt_cre=-${formatMonthYearTwoDigit}`}
          />
          {/*<NumberBox
            title="Confirmadas parcial"
            value={
              purchaseOrdersStatisticsMonth.current
                ? purchaseOrdersStatisticsMonth.current.confirmadas_parcial
                : 0
            }
            backColor={"--color-confirmadas-parcial"}
            url={`ordenes-compra/mantencion?ocest_nombre=Confirmada parcialmente&oc_dt_cre=-${formatMonthYearTwoDigit}`}
          />*/}
          <NumberBox
            title="Confirmadas"
            value={
              purchaseOrdersStatisticsMonth.current
                ? FORMAT_THOUSANDS(
                    purchaseOrdersStatisticsMonth.current.confirmadas,
                  )
                : 0
            }
            backColor={"--color-confirmadas"}
            url={`ordenes-compra/mantencion?ocest_nombre=Confirmada&oc_dt_cre=-${formatMonthYearTwoDigit}`}
          />
          <NumberBox
            title="Pendientes GG"
            value={
              purchaseOrdersStatisticsMonth.current
                ? purchaseOrdersStatisticsMonth.current.pendientes_gte
                : 0
            }
            backColor={"--color-pendientes-gte"}
            url={`ordenes-compra/mantencion?ocest_nombre=Pendiente GG&oc_dt_cre=-${formatMonthYearTwoDigit}`}
          />
          <NumberBox
            title="Confirmadas GG"
            value={
              purchaseOrdersStatisticsMonth.current
                ? FORMAT_THOUSANDS(
                    purchaseOrdersStatisticsMonth.current.confirmadas_gte,
                  )
                : 0
            }
            backColor={"--color-confirmadas-gte"}
            url={`ordenes-compra/mantencion?ocest_nombre=Confirmada GG&oc_dt_cre=-${formatMonthYearTwoDigit}`}
          />
        </div>
      );
    }
  };

  const renderPurchaseOrdersStatesYear = () => {
    if (purchaseOrdersStatisticsYear.current) {
      return (
        <div className="rowContainer">
          <NumberBox
            title="Totales"
            value={
              purchaseOrdersStatisticsYear.current
                ? FORMAT_THOUSANDS(purchaseOrdersStatisticsYear.current.total)
                : 0
            }
            backColor={"--primary-app-color"}
            url={`ordenes-compra/mantencion?ocest_nombre=&oc_dt_cre=${formatYear}`}
          />
          <NumberBox
            title="Pendientes"
            value={
              purchaseOrdersStatisticsYear.current
                ? purchaseOrdersStatisticsYear.current.pendientes
                : 0
            }
            backColor={"--color-pendiente"}
            url={`ordenes-compra/mantencion?ocest_nombre=Pendiente&oc_dt_cre=${formatYear}`}
          />
          <NumberBox
            title="Anuladas"
            value={
              purchaseOrdersStatisticsYear.current
                ? purchaseOrdersStatisticsYear.current.anuladas
                : 0
            }
            backColor={"--color-anuladas"}
            url={`ordenes-compra/mantencion?ocest_nombre=Anulada&oc_dt_cre=${formatYear}`}
          />
          {/*<NumberBox
            title="Rechazadas parcial"
            value={
              purchaseOrdersStatisticsYear.current
                ? purchaseOrdersStatisticsYear.current.rechazadas_parcial
                : 0
            }
            backColor={"--color-rechazadas-parcial"}
            url={`ordenes-compra/mantencion?ocest_nombre=Rechazada parcialmente&oc_dt_cre=${formatYear}`}
          />*/}
          <NumberBox
            title="Rechazadas"
            value={
              purchaseOrdersStatisticsYear.current
                ? purchaseOrdersStatisticsYear.current.rechazadas
                : 0
            }
            backColor={"--color-rechazadas"}
            url={`ordenes-compra/mantencion?ocest_nombre=Rechazada&oc_dt_cre=${formatYear}`}
          />
          {/*<NumberBox
            title="Aprobadas parcial"
            value={
              purchaseOrdersStatisticsYear.current
                ? purchaseOrdersStatisticsYear.current.aprobadas_parcial
                : 0
            }
            backColor={"--color-aprobadas-parcial"}
            url={`ordenes-compra/mantencion?ocest_nombre=Aprobada parcialmente&oc_dt_cre=${formatYear}`}
          />*/}
          <NumberBox
            title="Aprobadas"
            value={
              purchaseOrdersStatisticsYear.current
                ? purchaseOrdersStatisticsYear.current.aprobadas
                : 0
            }
            backColor={"--color-aprobadas"}
            url={`ordenes-compra/mantencion?ocest_nombre=Aprobada&oc_dt_cre=${formatYear}`}
          />
          {/*} <NumberBox
            title="Confirmadas parcial"
            value={
              purchaseOrdersStatisticsYear.current
                ? purchaseOrdersStatisticsYear.current.confirmadas_parcial
                : 0
            }
            backColor={"--color-confirmadas-parcial"}
            url={`ordenes-compra/mantencion?ocest_nombre=Confirmada parcialmente&oc_dt_cre=${formatYear}`}
          />*/}
          <NumberBox
            title="Confirmadas"
            value={
              purchaseOrdersStatisticsYear.current
                ? FORMAT_THOUSANDS(
                    purchaseOrdersStatisticsYear.current.confirmadas,
                  )
                : 0
            }
            backColor={"--color-confirmadas"}
            url={`ordenes-compra/mantencion?ocest_nombre=Confirmada&oc_dt_cre=${formatYear}`}
          />
          <NumberBox
            title="Pendientes GG"
            value={
              purchaseOrdersStatisticsYear.current
                ? purchaseOrdersStatisticsYear.current.pendientes_gte
                : 0
            }
            backColor={"--color-pendientes-gte"}
            url={`ordenes-compra/mantencion?ocest_nombre=Pendiente GG&oc_dt_cre=${formatYear}`}
          />
          <NumberBox
            title="Confirmadas GG"
            value={
              purchaseOrdersStatisticsYear.current
                ? FORMAT_THOUSANDS(
                    purchaseOrdersStatisticsYear.current.confirmadas_gte,
                  )
                : 0
            }
            backColor={"--color-confirmadas-gte"}
            url={`ordenes-compra/mantencion?ocest_nombre=Confirmada GG&oc_dt_cre=${formatYear}`}
          />
        </div>
      );
    }
  };

  const renderPptoStatisticsMonth = () => {
    if (
      jsonArrayGraphPptoDataBase.current &&
      jsonArrayGraphPptoData.current &&
      purchaseOrdersPptoStatisticsYear.current
    ) {
      return (
        <div className="rowContainer">
          <Radar
            data1={jsonArrayGraphPptoDataBase.current}
            data2={jsonArrayGraphPptoData.current}
            labels={labelsMonths}
            context="order"
          />
          <VerticalBar
            data1={jsonArrayGraphPptoDataBase.current}
            data2={jsonArrayGraphPptoData.current}
            labels={labelsMonths}
            context="order"
          />
        </div>
      );
    }
  };

  const renderPptoStatisticsYear = () => {
    if (
      jsonArrayGraphPptoDataBaseYear.current &&
      jsonArrayGraphPurchaseOrdersPptoYear.current &&
      purchaseOrdersPptoStatisticsYear.current
    ) {
      return (
        <div className="rowContainer">
          <Radar
            data1={jsonArrayGraphPptoDataBaseYear.current}
            data2={jsonArrayGraphPurchaseOrdersPptoYear.current}
            labels={labelsYear}
            context="order"
          />
          <VerticalBar
            data1={jsonArrayGraphPptoDataBaseYear.current}
            data2={jsonArrayGraphPurchaseOrdersPptoYear.current}
            labels={labelsYear}
            context="order"
          />
        </div>
      );
    }
  };

  return (
    <div>
      <Toast ref={toast} />
      <h4 className="topTitleLeft" style={{ textAlign: "left" }}>
        {initData.centros_costo_vista.includes(29) ? (
          <span>
            ORDENES DEL MES EN CURSO - SALMONES DE CHILE Y PRODUCTORA S.A
          </span>
        ) : initData.roles.some(
            (role: { codigo: string }) =>
              role.codigo === "APROC" || role.codigo === "CONFOC",
          ) == true && subroganteVal == true ? (
          <span>
            ORDENES DEL MES EN CURSO - ASIGNADAS A {initData.nombres}
            {initData.apellido_paterno} {initData.apellido_materno}
          </span>
        ) : (
          <span>ORDENES DEL MES EN CURSO - {CENTRO_COSTO_VISTA_USUARIO}</span>
        )}
      </h4>

      {isLoadingMonth ? null : renderPurchaseOrdersStatesMonth()}

      {isLoadingMonth && isLoadingPptoData ? null : renderPptoStatisticsMonth()}

      <h4 className="topTitleLeft" style={{ textAlign: "left" }}>
        {initData.centros_costo_vista.includes(29) ? (
          <span>
            ORDENES DEL AÑO EN CURSO - SALMONES DE CHILE Y PRODUCTORA S.A
          </span>
        ) : initData.roles.some(
            (role: { codigo: string }) =>
              role.codigo === "APROC" || role.codigo === "CONFOC",
          ) == true && subroganteVal == true ? (
          <span>
            ORDENES DEL AÑO EN CURSO - ASIGNADAS A {initData.nombres}
            {initData.apellido_paterno} {initData.apellido_materno}
          </span>
        ) : (
          <span>ORDENES DEL AÑO EN CURSO - {CENTRO_COSTO_VISTA_USUARIO}</span>
        )}
      </h4>

      {isLoadingYear ? null : renderPurchaseOrdersStatesYear()}

      {isLoadingYear && isLoadingPptoData ? null : renderPptoStatisticsYear()}

      {!isLoadingFooterDataRecents &&
      !isLoadingFooterDataCentros &&
      CENTRO_COSTO_USUARIO == 47 ? (
        <div className="rowContainer">
          <div style={{ width: "60%" }}>
            <h4>ORDENES DE COMPRA RECIENTES</h4>
            <TableRecentsStatistics
              dataRender={dataRecentOrdenes.current}
              operationType="recent-solicitudes"
            />
          </div>
          <div style={{ width: "40%" }}>
            <h4>CENTROS CON MÁS ORDENES DE COMPRA</h4>
            <TableRecentsStatistics
              dataRender={dataStatisticsOrdenesCentros.current}
              operationType="statistics-centros"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
